import { DISPLAY_CONFIG } from '../config/display';

// Supported stakes/targets have at most two decimals. Integer multiplication
// stays within Number's exact range for the server's 10,000 / 1,000,000 limits.
// Match PostgreSQL round(numeric, 2): round a positive half cent upwards.
export function calculateReturn(amount: number, multiplier: number): number {
  const product = Math.round(amount * 100) * Math.round(multiplier * 100);
  if (!Number.isSafeInteger(product) || product < 0) return 0;
  return Math.floor((product + 50) / 100) / 100;
}

// Local digit grouping (en-ZA: "1 234,50"), but no currency: these are play credits.
const creditFormatter = new Intl.NumberFormat(DISPLAY_CONFIG.locale, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactFormatter = new Intl.NumberFormat(DISPLAY_CONFIG.locale, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Reads an amount typed into a decimal field. iOS decimal keypads type the
 * region's separator, which is a comma in en-ZA and much of Europe, so both
 * "2,5" and "2.5" mean 2.5. Amounts never have more than two decimals, so a
 * comma followed by groups of three digits groups thousands, as the app itself
 * writes them: "10,000" is ten thousand, not ten. Anything else is NaN.
 */
export function parseAmountInput(value: string): number {
  const compact = value.replace(/\s/g, '');
  if (/^\d{1,3}(,\d{3})+(\.\d*)?$/.test(compact)) return Number(compact.replace(/,/g, ''));
  const normalised = /^\d*,\d{0,2}$/.test(compact) ? compact.replace(',', '.') : compact;
  return /^(\d+\.?\d*|\.\d+)$/.test(normalised) ? Number(normalised) : NaN;
}

/** Keeps only what an amount field can contain while the player types. */
export const amountInputChars = (value: string) => value.replace(/[^\d.,]/g, '');

export function formatMoney(value: number): string {
  const safeValue = Number.isFinite(value) ? value : 0;
  return `${DISPLAY_CONFIG.currencySymbol} ${creditFormatter.format(safeValue).replace(/\s/g, ' ')}`;
}

export function formatCompactMoney(value: number): string {
  const safeValue = Number.isFinite(value) ? value : 0;
  return `${DISPLAY_CONFIG.currencySymbol}${compactFormatter.format(safeValue)}`;
}
